import * as React from 'react'
import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'
import ThemeToggle from '../ui/ThemeToggle'
import LanguageToggle from '../ui/LanguageToggle'

interface DropdownItem {
  name: string;
  href: string;
  image?: string;
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showBanner, setShowBanner] = useState(true)
  const [isAboutOpen, setIsAboutOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const [isSectorsOpen, setIsSectorsOpen] = useState(false)
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null)
  
  // Mobile-specific dropdown state
  const location = useLocation()
  const navigate = useNavigate()
  const { t, isRTL } = useLanguage()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('.navigation-area')) {
        hideAllDropdowns()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (hoverTimeout) {
        clearTimeout(hoverTimeout)
      }
    }
  }, [hoverTimeout])

  // Close all dropdowns when location changes
  useEffect(() => {
    hideAllDropdowns() // Don't reset mobile dropdown on location change
    setIsMenuOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const aboutItems: DropdownItem[] = [
    { name: t('nav.aboutUs'), href: '/about', image: '/images/about/about-us.png' },
    { name: t('nav.csr'), href: '/csr', image: '/images/about/csr.png' },
    { name: t('nav.codeOfEthics'), href: '/code-of-ethics', image: '/images/about/code-of-ethics.png' },
    { name: t('nav.hse'), href: '/hse', image: '/images/about/hse.png' },
    { name: t('nav.qualityManagement'), href: '/quality-management', image: '/images/about/quality-management.png' },
    { name: t('nav.ourClients'), href: '/clients', image: '/images/about/our-clients.png' },
    { name: t('nav.ourVendors'), href: '/vendors', image: '/images/about/our-vendors.png' },
    { name: t('nav.documents'), href: '/documents', image: '/images/about/documents.png' },
  ]

  // Removed dropdown items - now using direct links

  const servicesList: DropdownItem[] = [
    { name: t('services.buildingInfrastructure'), href: '/services/building-infrastructure', image: '/images/services/Building-and-Infrastructure1.png' },
    { name: t('services.designEngineering'), href: '/services/design-engineering', image: '/images/services/engineering-and-design1.png' },
    { name: t('services.architecturalFitout'), href: '/services/architectural-fitout', image: '/images/services/architect-and-fitout1.png' },
    { name: t('services.civilWorks'), href: '/services/civil-works', image: '/images/services/civil-and-infrastructure1.png' },
    { name: t('services.mepWorks'), href: '/services/mep-works', image: '/images/services/mep-works1.png' },
    { name: t('services.dataCenters'), href: '/services/data-centers', image: '/images/services/datacenters.webp' },
    { name: t('services.pricingBidding'), href: '/services/pricing', image: '/images/services/pricing1.webp' },
    { name: t('services.maintenanceWorks'), href: '/services/maintenance', image: '/images/services/maintenance1.png' },
  ]

  const sectorsList: DropdownItem[] = [
    { name: t('sectors.commercial'), href: '/sectors/commercial', image: '/images/sectors-industries/commercial-ready1.png' },
    { name: t('sectors.entertainment'), href: '/sectors/entertainment', image: '/images/sectors-industries/entertainment-ready1.png' },
    { name: t('sectors.hotel'), href: '/sectors/hotel', image: '/images/sectors-industries/hotel-ready1.png' },
    { name: t('sectors.industrial'), href: '/sectors/industrial', image: '/images/sectors-industries/industry-ready1.png' },
    { name: t('sectors.residential'), href: '/sectors/residential', image: '/images/sectors-industries/residential-ready1.png' },
    { name: t('sectors.school'), href: '/sectors/school', image: '/images/sectors-industries/school-ready1.png' },
    { name: t('sectors.utilities'), href: '/sectors/utilities', image: '/images/sectors-industries/utilities-ready1.png' },
    { name: t('sectors.warehouse'), href: '/sectors/warehouse', image: '/images/sectors-industries/warehouse-ready1.png' },
  ]

  const navigation = [
    { name: t('nav.home'), href: '/', current: location.pathname === '/' },
    { name: t('nav.about'), href: '/about', current: location.pathname === '/about' || location.pathname === '/documents', hasDropdown: true, dropdownItems: aboutItems, dropdownKey: 'about' },
    { name: t('nav.ourProjects'), href: '/projects', current: location.pathname === '/projects' || location.pathname.startsWith('/projects') },
    { name: t('nav.ourServices'), href: '/our-services', current: location.pathname === '/our-services' || location.pathname.startsWith('/services/'), hasDropdown: true, dropdownItems: servicesList, dropdownKey: 'services' },
    { name: t('nav.sectorsIndustries'), href: '/industries-sectors', current: location.pathname === '/industries-sectors' || location.pathname.startsWith('/sectors/'), hasDropdown: true, dropdownItems: sectorsList, dropdownKey: 'sectors' },
    { name: t('nav.news'), href: '/blog', current: location.pathname === '/blog' },
  ]

  // Desktop navigation order (for RTL: Home first from right)
  const desktopNavigation = isRTL ? [
    { name: t('nav.news'), href: '/blog', current: location.pathname === '/blog' },
    { name: t('nav.sectorsIndustries'), href: '/industries-sectors', current: location.pathname === '/industries-sectors' || location.pathname.startsWith('/sectors/'), hasDropdown: true, dropdownItems: sectorsList, dropdownKey: 'sectors' },
    { name: t('nav.ourServices'), href: '/our-services', current: location.pathname === '/our-services' || location.pathname.startsWith('/services/'), hasDropdown: true, dropdownItems: servicesList, dropdownKey: 'services' },
    { name: t('nav.ourProjects'), href: '/projects', current: location.pathname === '/projects' || location.pathname.startsWith('/projects') },
    { name: t('nav.about'), href: '/about', current: location.pathname === '/about' || location.pathname === '/documents', hasDropdown: true, dropdownItems: aboutItems, dropdownKey: 'about' },
    { name: t('nav.home'), href: '/', current: location.pathname === '/' },
  ] : navigation

  // Mobile navigation order (for RTL: Home first from top)
  const mobileNavigation = isRTL ? [
    { name: t('nav.home'), href: '/', current: location.pathname === '/' },
    { name: t('nav.about'), href: '/about', current: location.pathname === '/about' || location.pathname === '/documents', hasDropdown: true, dropdownItems: aboutItems, dropdownKey: 'about' },
    { name: t('nav.ourProjects'), href: '/projects', current: location.pathname === '/projects' || location.pathname.startsWith('/projects') },
    { name: t('nav.ourServices'), href: '/our-services', current: location.pathname === '/our-services' || location.pathname.startsWith('/services/'), hasDropdown: true, dropdownItems: servicesList, dropdownKey: 'services' },
    { name: t('nav.sectorsIndustries'), href: '/industries-sectors', current: location.pathname === '/industries-sectors' || location.pathname.startsWith('/sectors/'), hasDropdown: true, dropdownItems: sectorsList, dropdownKey: 'sectors' },
    { name: t('nav.news'), href: '/blog', current: location.pathname === '/blog' },
  ] : navigation

  const closeBanner = () => {
    setShowBanner(false)
  }

  const toggleDropdown = (dropdownKey: string) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      setHoverTimeout(null)
    }

    if (dropdownKey === 'about') {
      setIsAboutOpen(prev => !prev)
      setIsServicesOpen(false)
      setIsSectorsOpen(false)
    } else if (dropdownKey === 'services') {
      setIsServicesOpen(prev => !prev)
      setIsAboutOpen(false)
      setIsSectorsOpen(false)
    } else if (dropdownKey === 'sectors') {
      setIsSectorsOpen(prev => !prev)
      setIsAboutOpen(false)
      setIsServicesOpen(false)
    }
  }

  const hideAllDropdowns = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      setHoverTimeout(null)
    }
    
    setIsAboutOpen(false)
    setIsServicesOpen(false)
    setIsSectorsOpen(false)
  }

  const handleMobileNavigation = (href: string) => {
    setIsMenuOpen(false)
    navigate(href)
  }


  const headerRef = React.useRef<HTMLElement | null>(null)

  useEffect(() => {
    const updateOffset = () => {
      const el = headerRef.current
      if (!el) return
      const height = el.getBoundingClientRect().height
      document.documentElement.style.setProperty('--header-offset', `${Math.ceil(height)}px`)
    }
    updateOffset()
    const ro = new ResizeObserver(updateOffset)
    if (headerRef.current) ro.observe(headerRef.current)
    window.addEventListener('resize', updateOffset)
    return () => {
      window.removeEventListener('resize', updateOffset)
      ro.disconnect()
    }
  }, [showBanner, isScrolled])

  return (
    <>
    <header ref={headerRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isRTL ? 'font-arabic' : 'font-english'} ${
      isScrolled 
        ? 'bg-black shadow-lg'
        : 'bg-black'
    }`}>
      
      {/* LinkedIn Banner */}
      {showBanner && (
        <div className="bg-stone-200 py-2">
          <div className={`max-w-7xl mx-auto px-4 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button 
              onClick={closeBanner}
              className="text-gray-600 hover:text-gray-800 transition-colors p-1 flex-shrink-0"
              aria-label="Close banner"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className={`flex-1 flex items-center justify-center ${isRTL ? 'pr-8' : 'pl-8'}`}>
              <div className="text-gray-800 text-sm text-center">
                <span className="font-bold">{t('linkedin.banner')} </span>
                <a 
                  href="https://www.linkedin.com/company/fmc-construction-co/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 font-bold hover:text-blue-700 transition-colors whitespace-nowrap"
                >
                  {t('follow.us')}
                </a>
              </div>
              
              <svg className={`w-5 h-5 text-blue-600 flex-shrink-0 ${isRTL ? 'mr-6' : 'ml-6'}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Main navigation */}
      <nav className={`transition-all duration-300 ${isScrolled ? 'py-3' : 'py-6'}`}>
        <div className="container-fmc">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Logo */}
            <Link to="/" className="flex items-center z-50">
              <img 
                src="/logos/logo.png" 
                alt="FMC Logo" 
                className="w-22 h-12 object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className={`navigation-area hidden lg:flex items-center space-x-1 ${isRTL ? 'space-x-reverse' : ''}`}>
              {desktopNavigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.hasDropdown ? (
                    <div className="relative">
                      <button
                        onClick={() => toggleDropdown(item.dropdownKey!)}
                        className={`px-4 py-2.5 font-medium transition-colors duration-200 rounded-md flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''} ${
                          item.current
                            ? 'text-blue-400 bg-blue-900/20'
                            : 'text-gray-300 hover:text-blue-400 hover:bg-gray-800'
                        }`}
                      >
                        {item.name}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          (item.dropdownKey === 'about' && isAboutOpen) || (item.dropdownKey === 'services' && isServicesOpen) || (item.dropdownKey === 'sectors' && isSectorsOpen) ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`px-4 py-2.5 font-medium transition-colors duration-200 rounded-md ${
                        item.current
                          ? 'text-blue-400 bg-blue-900/20'
                          : 'text-gray-300 hover:text-blue-400 hover:bg-gray-800'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                  
                  {/* Desktop Dropdowns */}
                  {item.hasDropdown && item.dropdownKey === 'about' && isAboutOpen && (
                    <div className={`absolute top-full mt-2 w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-50 ${isRTL ? 'right-0' : 'left-0'}`}>
                      {item.dropdownItems.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="px-4 py-3 text-sm text-gray-300 hover:bg-gray-700 transition-colors flex items-center"
                          style={isRTL ? { flexDirection: 'row-reverse', gap: '0.75rem' } : { gap: '0.75rem' }}
                        >
                          <span>{dropdownItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {item.hasDropdown && item.dropdownKey === 'services' && isServicesOpen && (
                    <div className={`absolute top-full mt-2 w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-50 ${isRTL ? 'right-0' : 'left-0'}`}>
                      {item.dropdownItems.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="px-4 py-3 text-sm text-gray-300 hover:bg-gray-700 transition-colors flex items-center"
                          style={isRTL ? { flexDirection: 'row-reverse', gap: '0.75rem' } : { gap: '0.75rem' }}
                        >
                          <img 
                            src={dropdownItem.image} 
                            alt={dropdownItem.name}
                            className="w-8 h-8 object-cover rounded-md flex-shrink-0"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          <span>{dropdownItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {item.hasDropdown && item.dropdownKey === 'sectors' && isSectorsOpen && (
                    <div className={`absolute top-full mt-2 w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-50 ${isRTL ? 'right-0' : 'left-0'}`}>
                      {item.dropdownItems.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="px-4 py-3 text-sm text-gray-300 hover:bg-gray-700 transition-colors flex items-center"
                          style={isRTL ? { flexDirection: 'row-reverse', gap: '0.75rem' } : { gap: '0.75rem' }}
                        >
                          <img 
                            src={dropdownItem.image} 
                            alt={dropdownItem.name}
                            className="w-8 h-8 object-cover rounded-md flex-shrink-0"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          <span>{dropdownItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right side controls */}
            <div className={`hidden lg:flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <ThemeToggle />
              <LanguageToggle />
              <Link 
                to="/contact" 
                className="px-5 py-2.5 text-white font-medium rounded-md transition-colors"
                style={{ backgroundColor: '#0075c9' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005a9e'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0075c9'}
              >
                {t('contact.us')}
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md transition-colors text-gray-300 hover:text-blue-400 hover:bg-gray-800"
              aria-label="Toggle menu"
              style={{ zIndex: 100001 }}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

    </header>

    {/* Mobile Navigation Sidebar - Outside Header */}
    <div className={`lg:hidden fixed inset-0 transition-all duration-300 ${
      isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
    }`} style={{ zIndex: 99999 }}>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={() => setIsMenuOpen(false)}
        style={{ zIndex: 99998 }}
      />
      
      {/* Sidebar Panel */}
      <div className={`absolute top-0 bottom-0 w-80 max-w-[85vw] transform transition-transform duration-300 ease-out font-arabic ${
        isRTL ? 'right-0' : 'left-0'
      } ${
        isMenuOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
      } bg-gray-900 shadow-2xl overflow-y-auto`} style={{ zIndex: 100000, backgroundColor: '#111827' }}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">FMC</span>
            </div>
            <span className="font-semibold text-lg text-white">
              {isRTL ? 'القائمة' : 'Menu'}
            </span>
          </div>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 rounded-lg transition-colors text-gray-400 hover:text-white hover:bg-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sidebar Content - POLISHED APPROACH */}
        <div className="p-4">
          {mobileNavigation.map((item) => (
            <div key={item.name} className="mb-4">
              {/* Main Item */}
              <div className="mb-2">
                <button
                  onClick={() => {
                    setIsMenuOpen(false)
                    window.location.href = item.href
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium text-base transition-all duration-200 ${
                    item.current
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  {item.name}
                </button>
              </div>

              {/* Dropdown Items - Compact */}
              {item.hasDropdown && item.dropdownItems && (
                <div className="ml-4 space-y-1">
                  {item.dropdownItems.map((dropdownItem) => (
                    <button
                      key={dropdownItem.name}
                      onClick={() => {
                        setIsMenuOpen(false)
                        window.location.href = dropdownItem.href
                      }}
                      className={`w-full text-left px-4 py-2 rounded-md text-sm transition-all duration-200 ${
                        location.pathname === dropdownItem.href
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      {dropdownItem.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {/* Sidebar Footer */}
          <div className="pt-3 mt-3 border-t border-gray-800 space-y-2">
            {/* Theme and Language Toggles */}
            <div className="flex items-center justify-between px-2 py-1">
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <LanguageToggle />
              </div>
            </div>

            {/* Contact Button */}
            <button
              onClick={() => handleMobileNavigation('/contact')}
              className="w-full px-4 py-2 text-white font-medium rounded-lg transition-all duration-200 shadow-sm text-sm"
              style={{ backgroundColor: '#0075c9' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005a9e'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0075c9'}
            >
              {t('contact.us')}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default Header